import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { ServerValidationError } from '@apple/utils/api/errors';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { saveVintage } from '@apple/features/vintage/api';
import { VintageForm } from '@apple/features/vintage/components/VintageForm';
import { vintageQueryKeys } from '@apple/features/vintage/queries';

export const Route = createFileRoute('/_authed/_admin/manage/vintages_/add')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.CustomerManagement',
		});
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation('vintage');
	const queryClient = useQueryClient();
	const goBackToList = useBackOrNavigate();

	const addVintageMutation = useMutation({
		mutationFn: saveVintage,
		onSuccess: async () => {
			notifications.show({ color: 'green', message: t('add.success'), icon: <Success /> });
			await queryClient.invalidateQueries({ queryKey: vintageQueryKeys.all });
			await goBackToList('/manage/vintages');
		},
		onError: (error: Error) => {
			if (!(error instanceof ServerValidationError)) {
				notifications.show({
					message: t('common:error.generic'),
					icon: <ErrorIcon />,
					color: 'red',
					autoClose: 5000,
				});
			}
		},
	});

	return (
		<DetailsLayout title={t('titles.add')}>
			<VintageForm
				initialStores={[]}
				initialProducts={[]}
				onSave={async values => {
					await addVintageMutation.mutateAsync(values);
				}}
				isSaving={addVintageMutation.isPending || addVintageMutation.isSuccess}
			/>
		</DetailsLayout>
	);
}
